import React from 'react';
import { arrayBuilderPages } from '~/platform/forms-system/src/js/patterns/array-builder';
import {
  arrayBuilderItemFirstPageTitleUI,
  arrayBuilderYesNoUI,
  arrayBuilderYesNoSchema,
  radioUI,
  radioSchema,
  fullNameUI,
  fullNameSchema,
  selectUI,
  textUI,
  checkboxUI,
  checkboxSchema,
  currentOrPastDateUI,
  currentOrPastDateSchema,
  titleUI,
  yesNoUI,
  yesNoSchema,
} from 'platform/forms-system/src/js/web-component-patterns';
import get from 'platform/utilities/data/get';
import titleCase from 'platform/utilities/data/titleCase';
import { replaceStrValues } from 'platform/shared/health-apps';
import {
  STATE_NAMES,
  STATE_VALUES,
  COUNTRY_NAMES,
  COUNTRY_VALUES,
  previousMarriageEndOptions,
} from '../../../../utils/labels';
import { handleVeteranMaxMarriagesAlert } from '../../../components/FormAlerts';
import { customTextSchema } from '../../../../config/definitions';
import { validations } from '../../../../config/validations';
import content from '../../../../utils/content.json';
import { createVeteranSpousePageTitle, formatDate } from '../../utils/helpers';

const showVeteranMarriagePage = formData =>
  formData.claimantRelationship === 'SURVIVING_SPOUSE' &&
  formData?.['view:hasAnyPreviousMarriages'] === true &&
  formData.hadPreviousMarriages?.veteran === true;

/**
 * Pages for Veteran's previous marriages (array-builder)
 */

function introDescription() {
  return (
    <div>
      <p className="vads-u-margin-top--0">
        Next we’ll ask you about the Veteran’s previous marriages. Fill this
        section out to the best of your knowledge. You may add up to 2
        marriages.
      </p>
      <p>
        <strong>Note:</strong> We won’t contact a previous spouse. This
        information is used to determine the validity of your marriage to the
        Veteran and we’re required by law to collect it.
      </p>
    </div>
  );
}

/** @type {ArrayBuilderOptions} */
// arrayPath is veteranMarriages because it's the Veteran's marriages being collected
const options = {
  arrayPath: 'veteranMarriages',
  nounSingular: content['household-previous-marriages-noun-singular'],
  nounPlural: content['household-previous-marriages-noun-plural'],
  required: true,
  maxItems: 2,
  isItemIncomplete: item =>
    !item?.spouseFullName?.first ||
    !item?.spouseFullName?.last ||
    (item?.reasonForSeparation === 'OTHER' && !item?.separationExplanation),
  text: {
    cancelAddTitle: replaceStrValues(
      content['array-cancel-add-title'],
      `${content['household-previous-marriages-noun-singular']}?`,
    ),
    cancelEditTitle: replaceStrValues(
      content['array-cancel-edit-title'],
      `${content['household-previous-marriages-noun-singular']}?`,
    ),
    cancelAddDescription: replaceStrValues(
      content['household-previous-marriages-cancel-add-description'],
      content['household-previous-marriages-veteran-previous-marriage-suffix'],
    ),
    cancelAddReviewDescription: replaceStrValues(
      content['household-previous-marriages-cancel-add-description'],
      content['household-previous-marriages-veteran-previous-marriage-suffix'],
    ),
    cancelEditDescription:
      content['household-previous-marriages-cancel-edit-description'],
    cancelEditReviewDescription:
      content['household-previous-marriages-cancel-edit-description'],
    cancelYes: content['array-cancel-add-yes'],
    cancelAddYes: content['array-cancel-add-yes'],
    cancelNo: content['array-cancel-add-no'],
    cancelAddNo: content['array-cancel-add-no'],
    cancelEditYes: content['array-cancel-edit-yes'],
    cancelEditNo: content['array-cancel-edit-no'],
    deleteDescription: replaceStrValues(
      content['household-previous-marriages-delete-description'],
      [
        content.the,
        content[
          'household-previous-marriages-veteran-previous-marriage-suffix'
        ],
      ],
    ),
    deleteNeedAtLeastOneDescription: content['array-empty-hint'],
    deleteNo: content['array-delete-no'],
    deleteTitle: replaceStrValues(
      content['array-delete-title'],
      `${content['household-previous-marriages-noun-singular']}?`,
    ),
    deleteYes: content['array-delete-yes'],
    alertMaxItems: handleVeteranMaxMarriagesAlert,
    getItemName: item => {
      const { first, middle, last, suffix } = get('spouseFullName', item) || {};
      const name = [first, middle, last, suffix].filter(Boolean).join(' ');
      return name || 'Previous spouse';
    },
    cardDescription: item => {
      return item?.dateOfMarriage || item?.dateOfSeparation ? (
        <div data-testid="card-date-range">
          {item?.dateOfMarriage ? formatDate(item?.dateOfMarriage) : ''} -{' '}
          {item?.dateOfSeparation ? formatDate(item?.dateOfSeparation) : ''}
        </div>
      ) : (
        ''
      );
    },
    summaryTitle: "Review the Veteran's previous marriages",
  },
};

const introPage = {
  uiSchema: {
    ...titleUI({
      title: content['household-veteran-previous-marriages-intro-title'],
      nounSingular: options.nounSingular,
      nounPlural: options.nounPlural,
    }),
    'ui:description': introDescription,
  },
  schema: {
    type: 'object',
    properties: {},
  },
};

/** @returns {PageSchema} */
const summaryPage = {
  uiSchema: {
    'view:wasMarriedBefore': arrayBuilderYesNoUI(
      options,
      {
        title: content['household-veteran-previous-marriages-summary-add'],
        hint: content['array-empty-hint'],
      },
      {
        title:
          content['household-veteran-previous-marriages-summary-add-another'],
        hint: content['array-empty-hint'],
      },
    ),
    veteranHasAdditionalMarriages: {
      ...yesNoUI({
        title:
          content['household-veteran-previous-marriages-summary-add-another'],
      }),
      'ui:required': formData => formData?.veteranMarriages?.length === 2,
      'ui:options': {
        hideIf: formData =>
          !formData?.veteranMarriages || formData?.veteranMarriages?.length < 2,
      },
    },
  },
  schema: {
    type: 'object',
    properties: {
      'view:wasMarriedBefore': arrayBuilderYesNoSchema,
      veteranHasAdditionalMarriages: yesNoSchema,
    },
    required: ['view:wasMarriedBefore'],
  },
};

const namePage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: 'Veteran’s previous spouse',
      nounSingular: options.nounSingular,
      nounPlural: options.nounPlural,
      lowerCase: false,
    }),
    spouseFullName: fullNameUI(),
  },
  schema: {
    type: 'object',
    properties: {
      spouseFullName: fullNameSchema,
    },
    required: ['spouseFullName'],
  },
};

/** @returns {PageSchema} */
const marriageDatePlacePage = {
  uiSchema: {
    'ui:title': createVeteranSpousePageTitle({
      getTitleText: (veteranName, spouseName) =>
        `When and where was ${veteranName} married to ${spouseName}?`,
    }),
    dateOfMarriage: currentOrPastDateUI({
      title: content['household-previous-marriages-date-location-date'],
      'ui:description': content['date-description'],
    }),
    marriedOutsideUs: checkboxUI({
      title: replaceStrValues(
        content['household-previous-marriages-date-location-outside-us'],
        titleCase(content.they),
      ),
    }),
    locationOfMarriage: {
      city: {
        ...textUI(content['address-city']),
      },
      state: {
        ...selectUI(content['address-state'], STATE_VALUES, STATE_NAMES),
        'ui:options': {
          hideIf: (formData, index) => {
            const item = formData?.veteranMarriages?.[index];
            const currentPageData = formData;
            return item?.marriedOutsideUs || currentPageData?.marriedOutsideUs;
          },
        },
      },
      otherCountry: {
        ...selectUI(content['address-country'], COUNTRY_VALUES, COUNTRY_NAMES),
        'ui:options': {
          hideIf: (formData, index) => {
            const item = formData?.veteranMarriages?.[index];
            const currentPageData = formData;
            return !(
              item?.marriedOutsideUs || currentPageData?.marriedOutsideUs
            );
          },
          labels: COUNTRY_VALUES.reduce((acc, value, idx) => {
            acc[value] = COUNTRY_NAMES[idx];
            return acc;
          }, {}),
        },
      },
    },
  },
  schema: {
    type: 'object',
    properties: {
      dateOfMarriage: currentOrPastDateSchema,
      marriedOutsideUs: checkboxSchema,
      locationOfMarriage: {
        type: 'object',
        properties: {
          city: {
            type: 'string',
            maxLength: 100,
          },
          state: {
            type: 'string',
            enum: STATE_VALUES,
            enumNames: STATE_NAMES,
            maxLength: 100,
          },
          otherCountry: {
            type: 'string',
            enum: COUNTRY_VALUES,
            enumNames: COUNTRY_NAMES,
            maxLength: 100,
          },
        },
      },
    },
  },
};

/** @returns {PageSchema} */
const endedPage = {
  uiSchema: {
    'ui:title': createVeteranSpousePageTitle({
      getTitleText: (veteranName, spouseName) =>
        `How did the marriage between ${veteranName} and ${spouseName} end?`,
    }),
    reasonForSeparation: radioUI({
      title: content['household-previous-marriages-separation-title'],
      labels: previousMarriageEndOptions,
    }),
    separationExplanation: textUI({
      title: content['household-previous-marriages-separation-explanation'],
      expandUnder: 'reasonForSeparation',
      expandUnderCondition: field => field === 'OTHER',
      required: (formData, index) => {
        const item = formData?.veteranMarriages?.[index];
        const currentPageData = formData;
        return (
          item?.reasonForSeparation === 'OTHER' ||
          currentPageData?.reasonForSeparation === 'OTHER'
        );
      },
    }),
  },
  schema: {
    type: 'object',
    properties: {
      reasonForSeparation: radioSchema(Object.keys(previousMarriageEndOptions)),
      separationExplanation: customTextSchema,
    },
  },
};

/** @returns {PageSchema} */
const marriageEndDateLocationPage = {
  uiSchema: {
    'ui:title': createVeteranSpousePageTitle({
      getTitleText: (veteranName, spouseName) =>
        `When and where did the marriage between ${veteranName} and ${spouseName} end?`,
    }),
    dateOfSeparation: currentOrPastDateUI({
      title: 'Date marriage ended',
      'ui:description': content['date-description'],
      validations: [validations.isAfterPreviousMarriageStartDate],
    }),
    marriageEndedOutsideUs: checkboxUI({
      title: replaceStrValues(
        content['household-previous-marriages-end-date-location-outside-us'],
        titleCase(content.their),
      ),
    }),
    locationOfSeparation: {
      city: {
        ...textUI(content['address-city']),
      },
      state: {
        ...selectUI(content['address-state'], STATE_VALUES, STATE_NAMES),
        'ui:options': {
          hideIf: (formData, index) => {
            const item = formData?.veteranMarriages?.[index];
            const currentPageData = formData;
            return (
              item?.marriageEndedOutsideUs ||
              currentPageData?.marriageEndedOutsideUs
            );
          },
        },
      },
      otherCountry: {
        ...selectUI(content['address-country'], COUNTRY_VALUES, COUNTRY_NAMES),
        'ui:options': {
          hideIf: (formData, index) => {
            const item = formData?.veteranMarriages?.[index];
            const currentPageData = formData;
            return !(
              item?.marriageEndedOutsideUs ||
              currentPageData?.marriageEndedOutsideUs
            );
          },
          labels: COUNTRY_VALUES.reduce((acc, value, idx) => {
            acc[value] = COUNTRY_NAMES[idx];
            return acc;
          }, {}),
        },
      },
    },
  },
  schema: {
    type: 'object',
    properties: {
      dateOfSeparation: currentOrPastDateSchema,
      marriageEndedOutsideUs: checkboxSchema,
      locationOfSeparation: {
        type: 'object',
        properties: {
          city: {
            type: 'string',
            maxLength: 100,
          },
          state: {
            type: 'string',
            enum: STATE_VALUES,
            enumNames: STATE_NAMES,
            maxLength: 100,
          },
          otherCountry: {
            type: 'string',
            enum: COUNTRY_VALUES,
            enumNames: COUNTRY_NAMES,
            maxLength: 100,
          },
        },
      },
    },
  },
};

export const veteranMarriagesPages = arrayBuilderPages(
  options,
  pageBuilder => ({
    veteranMarriagesIntro: pageBuilder.introPage({
      title: content['household-veteran-previous-marriages-intro-title'],
      path: 'veteran-previous-marriages',
      depends: formData => showVeteranMarriagePage(formData),
      uiSchema: introPage.uiSchema,
      schema: introPage.schema,
    }),
    veteranMarriagesSummary: pageBuilder.summaryPage({
      title: content['household-veteran-previous-marriages-summary-add'],
      path: 'veteran-previous-marriages/summary',
      depends: formData => showVeteranMarriagePage(formData),
      uiSchema: summaryPage.uiSchema,
      schema: summaryPage.schema,
    }),
    veteranPreviousSpouseName: pageBuilder.itemPage({
      title: 'Veteran’s previous spouse',
      path: 'veteran-previous-marriages/:index/spouse-name',
      depends: formData => showVeteranMarriagePage(formData),
      uiSchema: namePage.uiSchema,
      schema: namePage.schema,
    }),
    veteranMarriageDatePlace: pageBuilder.itemPage({
      title: createVeteranSpousePageTitle({
        getTitleText: (veteranName, spouseName) =>
          `When and where was ${veteranName} married to ${spouseName}?`,
      }),
      path: 'veteran-previous-marriages/:index/date-location',
      depends: formData => showVeteranMarriagePage(formData),
      uiSchema: marriageDatePlacePage.uiSchema,
      schema: marriageDatePlacePage.schema,
    }),
    veteranMarriageEnded: pageBuilder.itemPage({
      title: createVeteranSpousePageTitle({
        getTitleText: (veteranName, spouseName) =>
          `How did the marriage between ${veteranName} and ${spouseName} end?`,
      }),
      path: 'veteran-previous-marriages/:index/end-reason',
      depends: formData => showVeteranMarriagePage(formData),
      uiSchema: endedPage.uiSchema,
      schema: endedPage.schema,
    }),
    veteranMarriageEndDateLocation: pageBuilder.itemPage({
      title: createVeteranSpousePageTitle({
        getTitleText: (veteranName, spouseName) =>
          `When and where did the marriage between ${veteranName} and ${spouseName} end?`,
      }),
      path: 'veteran-previous-marriages/:index/end-date-location',
      depends: formData => showVeteranMarriagePage(formData),
      uiSchema: marriageEndDateLocationPage.uiSchema,
      schema: marriageEndDateLocationPage.schema,
    }),
  }),
);

export default veteranMarriagesPages;

export { options };
